import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
    BookOpen, Compass, GraduationCap, Layers, Network, Sparkles,
    Boxes, Link2, Search, ExternalLink,
} from 'lucide-react';
import { clsx } from 'clsx';
import { brandInputFocus } from '../components/brandChrome';
import { DOCUMENTATION_GLOSSARY } from '../data/documentationGlossary';
import { DOCUMENTATION_FEATURES, type FeatureDocEntry } from '../data/documentationFeatures';
import { DOCUMENTATION_CHAINS, type ChainDocEntry } from '../data/documentationChains';
import { NEW_FEATURES } from '../data/newFeatures';
import { getUiLocation } from '../components/tour/uiLocations';

const SECTIONS = [
    { id: 'features', label: 'Features', icon: Boxes },
    { id: 'chains', label: 'Chains & tokens', icon: Link2 },
    { id: 'changes', label: 'What changed', icon: Sparkles },
    { id: 'glossary', label: 'Glossary', icon: BookOpen },
    { id: 'guides', label: 'Guides', icon: Compass },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

const FEATURE_CATEGORIES = ['All', 'Workbench', 'Investigation', 'Cases', 'OSINT', 'Reports', 'Platform'] as const;

export function Documentation() {
    const [section, setSection] = useState<SectionId>('features');
    const [query, setQuery] = useState('');
    const [featureCategory, setFeatureCategory] = useState<(typeof FEATURE_CATEGORIES)[number]>('All');
    const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(DOCUMENTATION_FEATURES[0]?.id ?? null);
    const [selectedChainId, setSelectedChainId] = useState<string | null>(DOCUMENTATION_CHAINS[0]?.id ?? null);
    const [selectedChangeId, setSelectedChangeId] = useState<string | null>(NEW_FEATURES[0]?.id ?? null);

    const q = query.trim().toLowerCase();

    const filteredFeatures = useMemo(() => {
        return DOCUMENTATION_FEATURES.filter((f) => {
            if (featureCategory !== 'All' && f.category !== featureCategory) return false;
            if (!q) return true;
            const blob = [f.title, f.means, f.does, f.when, ...f.howTo, f.category].join(' ').toLowerCase();
            return blob.includes(q);
        });
    }, [q, featureCategory]);

    const filteredChains = useMemo(() => {
        return DOCUMENTATION_CHAINS.filter((c) => {
            if (!q) return true;
            const blob = [c.title, c.code, c.summary, c.leRelevance, c.howInProduct, ...c.tags].join(' ').toLowerCase();
            return blob.includes(q);
        });
    }, [q]);

    const filteredChanges = useMemo(() => {
        return NEW_FEATURES.filter((f) => {
            if (!q) return true;
            const blob = [f.title, f.blurb, f.howDone, f.whyDone, f.uxHelp, f.shipped].join(' ').toLowerCase();
            return blob.includes(q);
        });
    }, [q]);

    const glossaryEntries = useMemo(() => {
        return Object.entries(DOCUMENTATION_GLOSSARY)
            .filter(([term, def]) => !q || term.toLowerCase().includes(q) || def.toLowerCase().includes(q))
            .sort(([a], [b]) => a.localeCompare(b));
    }, [q]);

    const selectedFeature =
        filteredFeatures.find((f) => f.id === selectedFeatureId) ?? filteredFeatures[0] ?? null;
    const selectedChain =
        filteredChains.find((c) => c.id === selectedChainId) ?? filteredChains[0] ?? null;
    const selectedChange =
        filteredChanges.find((f) => f.id === selectedChangeId) ?? filteredChanges[0] ?? null;

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6" data-tour="documentation-page">
            <header className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">Documentation</h1>
                <p className="text-sm text-muted-foreground max-w-3xl">
                    Officer docupedia for CryptoFlow — what each feature means, how to use it, chain background
                    (with Wikipedia sources), and why recent changes exist. Not a marketing site.
                </p>
                <div className="relative max-w-xl">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search features, chains, changes, glossary…"
                        aria-label="Search documentation"
                        className={`w-full text-sm pl-9 pr-3 py-2 bg-secondary border border-border rounded-md ${brandInputFocus}`}
                    />
                </div>
            </header>

            <nav
                className="flex flex-wrap gap-2 border-b border-border pb-3"
                aria-label="Documentation sections"
                data-tour="documentation-sections"
            >
                {SECTIONS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => setSection(id)}
                        aria-pressed={section === id}
                        className={clsx(
                            'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border transition-colors',
                            section === id
                                ? 'bg-primary/10 text-primary border-primary/30 font-medium'
                                : 'bg-card text-muted-foreground border-border hover:bg-muted hover:text-foreground',
                        )}
                    >
                        <Icon className="w-3.5 h-3.5" aria-hidden />
                        {label}
                    </button>
                ))}
            </nav>

            {section === 'features' && (
                <DocSplit
                    listLabel="Features"
                    list={
                        <div className="space-y-3">
                            <div className="flex flex-wrap gap-1.5">
                                {FEATURE_CATEGORIES.map((cat) => (
                                    <button
                                        key={cat}
                                        type="button"
                                        onClick={() => setFeatureCategory(cat)}
                                        className={clsx(
                                            'px-2 py-0.5 text-[11px] rounded-md border',
                                            featureCategory === cat
                                                ? 'bg-primary/10 text-primary border-primary/30'
                                                : 'border-border text-muted-foreground hover:bg-muted',
                                        )}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                            <DocList
                                empty="No matching features."
                                items={filteredFeatures.map((f) => ({
                                    id: f.id,
                                    title: f.title,
                                    meta: f.category,
                                    active: selectedFeature?.id === f.id,
                                    onSelect: () => setSelectedFeatureId(f.id),
                                }))}
                            />
                        </div>
                    }
                    detail={selectedFeature ? <FeatureDetail feature={selectedFeature} /> : <EmptyDetail />}
                />
            )}

            {section === 'chains' && (
                <DocSplit
                    listLabel="Chains & tokens"
                    list={
                        <DocList
                            empty="No matching chains."
                            items={filteredChains.map((c) => ({
                                id: c.id,
                                title: c.title,
                                meta: c.code,
                                active: selectedChain?.id === c.id,
                                onSelect: () => setSelectedChainId(c.id),
                            }))}
                        />
                    }
                    detail={selectedChain ? <ChainDetail chain={selectedChain} /> : <EmptyDetail />}
                />
            )}

            {section === 'changes' && (
                <section className="space-y-3" data-tour="documentation-new-features" aria-labelledby="doc-changes">
                    <div className="space-y-1">
                        <h2 id="doc-changes" className="text-lg font-semibold text-foreground">What changed</h2>
                        <p className="text-sm text-muted-foreground">
                            How each change was made, why, and how it helps UX. Version emails still live in{' '}
                            <Link className="text-primary hover:underline" to="/settings">Settings → What’s New</Link>.
                        </p>
                    </div>
                    <DocSplit
                        listLabel="Changes"
                        list={
                            <DocList
                                empty="No matching changes."
                                items={filteredChanges.map((f) => ({
                                    id: f.id,
                                    title: f.title,
                                    meta: f.shipped,
                                    active: selectedChange?.id === f.id,
                                    onSelect: () => setSelectedChangeId(f.id),
                                }))}
                            />
                        }
                        detail={selectedChange ? (
                            <article className="space-y-4 text-sm">
                                <header className="space-y-1">
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{selectedChange.shipped}</p>
                                    <h3 className="text-lg font-semibold text-foreground">{selectedChange.title}</h3>
                                    <p className="text-muted-foreground leading-relaxed">{selectedChange.blurb}</p>
                                </header>
                                <DetailBlock title="How it was done">{selectedChange.howDone}</DetailBlock>
                                <DetailBlock title="Why">{selectedChange.whyDone}</DetailBlock>
                                <DetailBlock title="How it helps UX">{selectedChange.uxHelp}</DetailBlock>
                                <DetailLinks href={selectedChange.href} locate={selectedChange.locate} />
                            </article>
                        ) : <EmptyDetail />}
                    />
                </section>
            )}

            {section === 'glossary' && (
                <section className="space-y-3" aria-labelledby="doc-glossary" data-tour="documentation-glossary">
                    <h2 id="doc-glossary" className="text-lg font-semibold text-foreground">
                        Glossary ({glossaryEntries.length})
                    </h2>
                    <div className="rounded-lg border border-border bg-card divide-y divide-border max-h-[32rem] overflow-y-auto">
                        {glossaryEntries.length === 0 ? (
                            <p className="text-sm text-muted-foreground px-4 py-6">No matching terms.</p>
                        ) : (
                            glossaryEntries.map(([term, definition]) => (
                                <details key={term} className="group px-4 py-2.5">
                                    <summary className="text-sm font-medium text-foreground cursor-pointer hover:text-primary list-none">
                                        {term}
                                    </summary>
                                    <p className="text-sm text-muted-foreground leading-relaxed mt-1.5">{definition}</p>
                                </details>
                            ))
                        )}
                    </div>
                </section>
            )}

            {section === 'guides' && (
                <section className="space-y-6" aria-labelledby="doc-guides">
                    <h2 id="doc-guides" className="text-lg font-semibold text-foreground">Guides</h2>

                    <div className="space-y-3">
                        <h3 className="text-base font-semibold text-foreground">Getting started</h3>
                        <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                            <li>Open <Link className="text-primary hover:underline" to="/workbench">Workbench</Link> and paste a wallet, tx hash, or lead into the Omnibar.</li>
                            <li>Confirm the chain badge, run triage, then Trace / Follow the Money for a gold path.</li>
                            <li>Select a node → Node Inspector → Analysis → Targeted Node Intelligence for Auto-Investigate and the LE Report.</li>
                            <li>Practice on <Link className="text-primary hover:underline" to="/demos">Demos</Link> before spending live API quota.</li>
                        </ol>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <DocQuickLink to="/workbench" icon={Network} title="Workbench" blurb="Live investigation canvas." />
                            <DocQuickLink to="/demos" icon={GraduationCap} title="Demos" blurb="Guided scenarios." />
                            <DocQuickLink to="/cases" icon={Layers} title="Fusion Canvas" blurb="Join OSINT onto a case graph (Cases → Fusion Canvas)." />
                        </div>
                    </div>

                    <div className="space-y-3">
                        <h3 className="text-base font-semibold text-foreground">Investigation guide</h3>
                        <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                            <p>
                                Auto-Investigate runs exchange detection, velocity, counterparties, exposure, and traces
                                sequentially, then builds a signal assessment for the LE Report. Prefer the SSE stream;
                                if it fails, the UI falls back to per-step POSTs automatically.
                            </p>
                            <ol className="list-decimal list-inside space-y-2">
                                <li>Attach work to a <Link className="text-primary hover:underline" to="/cases">Case</Link> when the complaint has a file number.</li>
                                <li>On Case Detail → Workbench, choose Resume / Blank / Open saved — do not guess.</li>
                                <li>Paste the primary wallet → confirm chain → Investigate.</li>
                                <li>After the run, open View LE Report for the printable brief.</li>
                                <li>For Instagram / Telegram / P2P leads, harvest on those desks then join via Fusion Canvas.</li>
                            </ol>
                            <p>
                                Definitions while reading demos: Glossary tab, or header Help → Crypto FAQ
                                ({getUiLocation('dashboard-faq').where}).
                            </p>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}

function DocSplit({
    list,
    detail,
}: {
    listLabel?: string;
    list: ReactNode;
    detail: ReactNode;
}) {
    return (
        <div className="grid lg:grid-cols-[minmax(0,17rem)_1fr] gap-4 items-start">
            <aside className="rounded-lg border border-border bg-card p-3 min-h-[16rem]">{list}</aside>
            <div className="rounded-lg border border-border bg-card p-5 min-h-[16rem]">{detail}</div>
        </div>
    );
}

function DocList({
    items,
    empty,
}: {
    items: { id: string; title: string; meta?: string; active: boolean; onSelect: () => void }[];
    empty: string;
}) {
    if (items.length === 0) {
        return <p className="text-sm text-muted-foreground px-1 py-4">{empty}</p>;
    }
    return (
        <ul className="space-y-1 max-h-[28rem] overflow-y-auto">
            {items.map((item) => (
                <li key={item.id}>
                    <button
                        type="button"
                        onClick={item.onSelect}
                        className={clsx(
                            'w-full text-left px-2.5 py-2 rounded-md text-sm transition-colors',
                            item.active ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground',
                        )}
                    >
                        <span className="block font-medium truncate">{item.title}</span>
                        {item.meta && (
                            <span className="block text-[11px] text-muted-foreground mt-0.5">{item.meta}</span>
                        )}
                    </button>
                </li>
            ))}
        </ul>
    );
}

function FeatureDetail({ feature }: { feature: FeatureDocEntry }) {
    return (
        <article className="space-y-4 text-sm" data-tour="documentation-feature-detail">
            <header className="space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{feature.category}</p>
                <h3 className="text-lg font-semibold text-foreground">{feature.title}</h3>
            </header>
            <DetailBlock title="What it means">{feature.means}</DetailBlock>
            <DetailBlock title="What it does">{feature.does}</DetailBlock>
            <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">How to use it</h4>
                <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground leading-relaxed">
                    {feature.howTo.map((step) => (
                        <li key={step}>{step}</li>
                    ))}
                </ol>
            </div>
            <DetailBlock title="When to use it">{feature.when}</DetailBlock>
            <DetailLinks href={feature.href} locate={feature.locate} />
        </article>
    );
}

function ChainDetail({ chain }: { chain: ChainDocEntry }) {
    return (
        <article className="space-y-4 text-sm" data-tour="documentation-chain-detail">
            <header className="space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono">{chain.code}</p>
                <h3 className="text-lg font-semibold text-foreground">{chain.title}</h3>
            </header>
            <DetailBlock title="Background">{chain.summary}</DetailBlock>
            <DetailBlock title="Why it matters for LE">{chain.leRelevance}</DetailBlock>
            <DetailBlock title="How CryptoFlow uses it">{chain.howInProduct}</DetailBlock>
            <DetailBlock title="Address hints">{chain.addressHints}</DetailBlock>
            <div className="flex flex-wrap gap-2">
                {chain.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-[11px] rounded-md bg-muted text-muted-foreground border border-border">
                        {tag}
                    </span>
                ))}
            </div>
            <a
                href={chain.wikipediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-primary hover:underline text-sm"
            >
                Wikipedia source <ExternalLink className="w-3.5 h-3.5" aria-hidden />
            </a>
            <p className="text-xs text-muted-foreground">{chain.sourceNote}</p>
        </article>
    );
}

function DetailBlock({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{title}</h4>
            <p className="text-muted-foreground leading-relaxed">{children}</p>
        </div>
    );
}

function DetailLinks({ href, locate }: { href?: string; locate?: Parameters<typeof getUiLocation>[0] }) {
    const where = locate ? getUiLocation(locate).where : null;
    if (!href && !where) return null;
    return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs pt-1 border-t border-border">
            {href && (
                <Link className="text-primary hover:underline" to={href}>Open in app</Link>
            )}
            {where && <span className="text-muted-foreground">Where: {where}</span>}
        </div>
    );
}

function EmptyDetail() {
    return <p className="text-sm text-muted-foreground">Select an entry from the list.</p>;
}

function DocQuickLink({
    to,
    icon: Icon,
    title,
    blurb,
}: {
    to: string;
    icon: typeof Network;
    title: string;
    blurb: string;
}) {
    return (
        <Link
            to={to}
            className="rounded-lg border border-border bg-card px-4 py-3 hover:border-primary/40 hover:bg-muted/40 transition-colors block"
        >
            <div className="flex items-center gap-2 text-foreground font-medium text-sm">
                <Icon className="w-4 h-4 text-primary" aria-hidden />
                {title}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{blurb}</p>
        </Link>
    );
}
